import { ImageScaleView, ViewOpts } from '@play-co/timestep-core/ui';
import { TextInput } from 'src/game/components/shared/TextInput';
import i18n from 'src/lib/i18n/i18n';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';

interface TeamInputFieldOpts extends ViewOpts {
  fontSize?: string;
  placeholder?: string;
  multiline?: boolean;
  onChange?: (valid: boolean) => void;
  allowClear?: boolean;
  minLength?: number;
  maxLength?: number;
}

export default class TeamInputField extends ImageScaleView {
  public get text() {
    return this.inputField.text;
  }
  public readonly inputField: TextInput;
  private readonly clearButton?: ButtonScaleView;
  private readonly onChange?: (valid: boolean) => void;
  private readonly options: TeamInputFieldOpts;

  constructor(opts: TeamInputFieldOpts) {
    super({
      ...opts,
      image: 'assets/ui/match2/team/team_text_field.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 34, right: 34 },
        vertical: { top: 35, bottom: 35 },
      },
      width: opts.width ?? 370,
      height: opts.height ?? 69,
      offsetY: -69 * 0.5,
    });

    this.options = opts;

    const width = this.style.width * 0.88;
    const height = this.style.height - (opts.multiline ? 40 : 15);
    const paddingX = (this.style.width - width) * 0.5;
    const paddingY = (this.style.height - height) * 0.5;
    const placeholder = opts.placeholder ? i18n(opts.placeholder) : undefined;

    this.inputField = new TextInput({
      superview: this,
      width: width - (opts.allowClear ? 35 : 0),
      height,
      x: paddingX,
      y: paddingY,
      placeholder,
      multiline: opts.multiline,
      minLength: opts.minLength,
      maxLength: opts.maxLength,
    });

    if (opts.allowClear) {
      this.clearButton = new ButtonScaleView({
        superview: this,
        x: this.style.width - 38,
        y: this.style.height * 0.5 + 2,
        image: 'assets/ui/match2/team/icon_clear.png',
        width: 24,
        height: 28,
        centerOnOrigin: true,
        onClick: async () => {
          sounds.sfx.play('ok');
          this.inputField.text = '';
          this.inputField.validate();
        },
        tracking: {
          feature: Feature.MATCH2.TEAM._,
          $subFeature: Feature.MATCH2.TEAM.TAB_SEARCH_CLEAR,
        },
      });
    }

    this.onChange = opts.onChange;
    this.inputField.on('InputValidated', this.handleChange.bind(this));
    this.handleChange();
  }

  private handleChange(valid?: boolean) {
    const value = this.inputField.text;
    this.onChange?.(!!valid);
    this.inputField.updateOpts({
      inputStyle: {
        color: value.length ? '#32383D' : '#89959E',
        textAlign: 'left',
        fontWeight: 'bold',
        fontSize: `${this.options.fontSize ?? `2.5vh`}`,
        font: 'Body',
        resize: 'none',
      },
    });
    this.clearButton?.updateOpts({ visible: !!value.length });
  }
}
